import type { Exercise } from '../../types/exercise'
import type {
  CellPayload,
  ExercisePayload,
  TrainingPlan,
  TrainingPlanWeekKind,
  WeekPayload,
} from '../../types/training-plan'

export interface MatrixRow {
  exercise_id: number
  exercise: Exercise
  position: number
}

export interface MatrixWeek {
  week_number: number
  kind: TrainingPlanWeekKind
  description: string | null
}

export interface MatrixState {
  rows: MatrixRow[]
  weeks: MatrixWeek[]
  /** Frequency per cell, keyed by cellKey(week_number, exercise_id). */
  frequencies: Record<string, number>
}

export function cellKey(weekNumber: number, exerciseId: number): string {
  return `${weekNumber}:${exerciseId}`
}

export interface PlanCategory {
  id: string
  name: string
  position: number
}

// Single source of truth for category colours, shared by the matrix squares,
// the minutes area chart and the radar chart so a category looks the same
// everywhere. Assigned by the category's order in the plan.
export const CATEGORY_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f97316', '#a855f7',
  '#eab308', '#06b6d4', '#ec4899', '#14b8a6', '#8b5cf6',
]

/** Distinct categories present in the matrix, ordered by category position. */
export function planCategories(matrix: MatrixState): PlanCategory[] {
  const byId = new Map<string, PlanCategory>()
  for (const row of matrix.rows) {
    const category = row.exercise.category
    if (category && !byId.has(category.id)) {
      byId.set(category.id, { id: category.id, name: category.name, position: category.position })
    }
  }
  return [...byId.values()].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
}

/** Maps each present category id to its colour (stable across the whole plan UI). */
export function categoryColorMap(matrix: MatrixState): Map<string, string> {
  const map = new Map<string, string>()
  planCategories(matrix).forEach((category, i) => {
    map.set(category.id, CATEGORY_COLORS[i % CATEGORY_COLORS.length])
  })
  return map
}

/** Builds the editable matrix from a loaded plan. */
export function planToMatrix(plan: TrainingPlan): MatrixState {
  const rows: MatrixRow[] = [...plan.exercises]
    .sort((a, b) => a.position - b.position)
    .map((e) => ({ exercise_id: e.exercise_id, exercise: e.exercise, position: e.position }))

  const weeks: MatrixWeek[] = [...plan.weeks]
    .sort((a, b) => a.week_number - b.week_number)
    .map((w) => ({ week_number: w.week_number, kind: w.kind, description: w.description }))

  const frequencies: Record<string, number> = {}
  for (const cell of plan.cells) {
    frequencies[cellKey(cell.week_number, cell.exercise_id)] = cell.frequency
  }

  return { rows, weeks, frequencies }
}

/** Snapshot of only the non-zero frequencies, used to diff for regeneration. */
export function frequencySnapshot(matrix: MatrixState): Record<string, number> {
  const snapshot: Record<string, number> = {}
  for (const [key, value] of Object.entries(matrix.frequencies)) {
    if (value > 0) snapshot[key] = value
  }
  return snapshot
}

/**
 * Weeks whose generated workouts would change versus the loaded snapshot:
 * any cell that changed frequency, plus columns touched by added/removed rows.
 */
export function computeAffectedWeeks(
  initial: Record<string, number>,
  matrix: MatrixState
): number[] {
  const currentExerciseIds = new Set(matrix.rows.map((r) => r.exercise_id))
  const current: Record<string, number> = {}
  for (const row of matrix.rows) {
    for (const week of matrix.weeks) {
      const freq = matrix.frequencies[cellKey(week.week_number, row.exercise_id)] ?? 0
      if (freq > 0) current[cellKey(week.week_number, row.exercise_id)] = freq
    }
  }

  const affected = new Set<number>()
  const keys = new Set([...Object.keys(initial), ...Object.keys(current)])
  for (const key of keys) {
    const [weekStr, exerciseStr] = key.split(':')
    const week = Number(weekStr)
    const exerciseId = Number(exerciseStr)
    const before = initial[key] ?? 0
    const after = currentExerciseIds.has(exerciseId) ? (current[key] ?? 0) : 0
    if (before !== after) affected.add(week)
  }
  return [...affected].sort((a, b) => a - b)
}

export interface MatrixPayloadParts {
  weeks: WeekPayload[]
  exercises: ExercisePayload[]
  cells: CellPayload[]
}

/**
 * Serializes the matrix into the API payload. Cells are emitted when they are
 * non-zero now or were non-zero before, so cleared frequencies are sent as 0
 * (the backend upserts cells and never deletes omitted ones).
 */
export function matrixToPayload(
  matrix: MatrixState,
  initial: Record<string, number>
): MatrixPayloadParts {
  const weeks: WeekPayload[] = matrix.weeks.map((w) => ({
    week_number: w.week_number,
    kind: w.kind,
    description: w.description,
  }))

  const exercises: ExercisePayload[] = matrix.rows.map((row, index) => ({
    exercise_id: row.exercise_id,
    position: index,
  }))

  const cells: CellPayload[] = []
  for (const row of matrix.rows) {
    for (const week of matrix.weeks) {
      const key = cellKey(week.week_number, row.exercise_id)
      const current = matrix.frequencies[key] ?? 0
      const before = initial[key] ?? 0
      if (current > 0 || before > 0) {
        cells.push({
          week_number: week.week_number,
          exercise_id: row.exercise_id,
          frequency: current,
        })
      }
    }
  }

  return { weeks, exercises, cells }
}
